// Backend is hosted GlitchTip (Sentry-wire-compatible). DSN points at app.glitchtip.com.
import * as Sentry from '@sentry/react'
import { Capacitor } from '@capacitor/core'
import { collectionOf, firestoreErrorContext } from './firestoreErrorContext'
import { isIndexedDbBreakage, isSideDatabaseClosing, noteIndexedDbBreakage } from './idbBreakage'
import { log } from './logger'
import { isChunkLoadErrorMessage } from './chunkLoadError'
import { isInstallationsNetworkErrorMessage } from './installationsNetworkError'
import { fetchFailureTags, hostOf, isBareNetworkErrorMessage, noteFetchFailure } from './fetchFailures'
import { isRestartInFlight } from './webViewRestart'

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  // E2E runs against the emulators and trips denials on purpose, and the dev
  // server's own HMR/module-reload failures aren't app bugs; keep both out of GlitchTip.
  // So is a production build served locally (`vite preview`, headless tooling).
  if (!dsn || import.meta.env.DEV || import.meta.env.MODE === 'e2e' || isLocalWebBuild()) return

  Sentry.init({
    dsn,
    release: import.meta.env.VITE_APP_RELEASE,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    beforeSend: (event, hint) => {
      if (isFirestoreShutdownError(event) || isResizeObserverLoopError(event) || isInstallationsNetworkError(event)) return null
      if (isRestartInFlight() && isAbortedCallableError(event)) return null
      if (isChunkLoadEvent(event)) {
        // A stale-chunk reload is already recovering; a give-up is one issue, not one per hash.
        if (isRestartInFlight()) return null
        event.fingerprint = ['chunk-load-failed']
        return event
      }
      if (isSideDatabaseClosing(event)) {
        log('idb', 'side database closing — not reported', { error: event.exception?.values?.[0]?.value, closed: event.tags?.idb_closed })
        return null
      }
      if (isIndexedDbBreakage(event)) {
        // The outgoing WebView's Firestore breaks while a restart tears it down
        // (#406); a recovery restart already reported the breakage that caused it.
        if (isRestartInFlight()) {
          log('idb', 'breakage during restart — not reported', { error: event.exception?.values?.[0]?.value })
          return null
        }
        noteIndexedDbBreakage(event.exception?.values?.[0]?.value?.slice(0, 300) ?? 'unknown')
        event.fingerprint = ['indexeddb-broken']
        return event
      }
      if (isBareNetworkErrorMessage(event.exception?.values?.[0]?.value ?? '')) {
        event.tags = { ...event.tags, ...fetchFailureTags(hint.originalException) }
      }
      const ctx = firestoreErrorContext(hint.originalException)
      if (ctx) {
        const collection = collectionOf(ctx.path)
        event.tags = { ...event.tags, fs_op: ctx.op, fs_path: ctx.path, fs_collection: collection }
        // Split "Missing or insufficient permissions" & co. into one issue per
        // operation + collection instead of one bucket for the whole app.
        event.fingerprint = ['{{ default }}', ctx.op, collection]
      }
      return event
    },
    // Capgo logs chattily on every boot and would crowd the useful history out
    // of the 100-breadcrumb buffer.
    beforeBreadcrumb: (crumb, hint) => {
      if (crumb.category === 'fetch' && hint?.data instanceof Error) noteFetchFailure(hostOf(hint.input), hint.data)
      return crumb.category === 'console' && crumb.message?.includes('CapgoUpdater') ? null : crumb
    },
  })

  // release = commit SHA (source-map identity). app_version = semver, for human
  // grouping in GlitchTip — the same semver can run on many native builds via OTA.
  Sentry.setTag('app_version', import.meta.env.VITE_BUNDLE_VERSION)
}

// Android's native shell also serves from http://localhost, so only the web counts.
function isLocalWebBuild(): boolean {
  return !Capacitor.isNativePlatform() && ['localhost', '127.0.0.1'].includes(location.hostname)
}

// The OTA native restart terminates Firestore on purpose (ota.ts); every read
// still in flight then rejects with one of these. Expected, not a bug.
const FIRESTORE_SHUTDOWN = /Firestore shutting down|client has already been terminated/

function isFirestoreShutdownError(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(v => FIRESTORE_SHUTDOWN.test(v.value ?? ''))
}

// The same restart tears down the WebView under any callable still in flight;
// the Functions SDK reports the dropped request (HTTP status 0) as `internal`.
// The restored route re-runs the call in the new WebView.
const ABORTED_CALLABLE = /^internal\b/

function isAbortedCallableError(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(v => v.type === 'FirebaseError' && ABORTED_CALLABLE.test(v.value ?? ''))
}

// The browser reports a ResizeObserver callback that resized observed elements
// again as an error, then delivers those notifications next frame. Benign.
const RESIZE_OBSERVER_LOOP = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/

function isChunkLoadEvent(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(v => isChunkLoadErrorMessage(v.value ?? ''))
}

function isResizeObserverLoopError(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(v => RESIZE_OBSERVER_LOOP.test(v.value ?? ''))
}

function isInstallationsNetworkError(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(v => isInstallationsNetworkErrorMessage(v.value ?? ''))
}

export { Sentry }
